/**
 * useCameraStream — camera permission + stream lifecycle for CameraCapture.
 *
 * Handles every failure mode the user kept hitting:
 *   - Running inside an iframe without `allow="camera"` (preview editor)
 *   - Permission previously denied ("bloqueado" en Chrome/Edge/Brave/Safari/Firefox)
 *   - Non-HTTPS / insecure context
 *   - No cameras plugged in
 *   - Browsers that block getUserMedia with `facingMode: exact` constraints
 *
 * phase: idle | requesting | live | blocked | unavailable | insecure | iframe
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { devError } from "@/lib/log";

export function useCameraStream(open) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [phase, setPhase] = useState("idle");
  const [errMsg, setErrMsg] = useState("");
  const [devices, setDevices] = useState([]);
  const [attempt, setAttempt] = useState(0);

  const inIframe = typeof window !== "undefined" && window.self !== window.top;
  const isSecure =
    typeof window !== "undefined" &&
    (window.isSecureContext ||
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const start = useCallback(async () => {
    setErrMsg("");
    setPhase("requesting");

    // Sanity checks
    if (!isSecure) {
      setPhase("insecure");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setPhase("unavailable");
      setErrMsg("Tu navegador no soporta acceso a cámara en este dispositivo.");
      return;
    }

    // Pre-probe permission state if supported (Chromium, modern Firefox/Safari)
    if (navigator.permissions?.query) {
      try {
        const st = await navigator.permissions.query({ name: "camera" });
        if (st.state === "denied") {
          setPhase("blocked");
          return;
        }
      } catch {
        /* permission API not available for 'camera' in some browsers — ignore and try */
      }
    }

    try {
      const constraints = {
        audio: false,
        video: {
          facingMode: { ideal: facing },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (inner) {
        // Retry with the loosest possible constraints (old Android / kiosk cams)
        if (
          inner?.name === "OverconstrainedError" ||
          inner?.name === "ConstraintNotSatisfiedError" ||
          inner?.name === "NotReadableError"
        ) {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        } else {
          throw inner;
        }
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      setPhase("live");

      // Enumerate after stream is active so labels are populated
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        setDevices(list.filter((d) => d.kind === "videoinput"));
      } catch {
        /* ignore */
      }
    } catch (e) {
      devError("camera error", e);
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
        setPhase(inIframe ? "iframe" : "blocked");
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setPhase("unavailable");
        setErrMsg("No encontramos ninguna cámara conectada a este dispositivo.");
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        setPhase("unavailable");
        setErrMsg("Otra app ya está usando la cámara. Cierra Zoom, Meet, Teams o cualquier app de video y reintenta.");
      } else {
        setPhase("unavailable");
        setErrMsg(e?.message || `No se pudo abrir la cámara (${name || "error desconocido"}).`);
      }
    }
  }, [facing, inIframe, isSecure]);

  useEffect(() => {
    if (!open) return;
    start();
    return () => {
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing, attempt]);

  useEffect(() => {
    // Reset state when closed so next open starts clean
    if (!open) {
      setPhase("idle");
      setDevices([]);
    }
  }, [open]);

  const flip = useCallback(() => {
    stopStream();
    setFacing((f) => (f === "environment" ? "user" : "environment"));
  }, [stopStream]);

  const retry = useCallback(() => {
    stopStream();
    setAttempt((a) => a + 1);
  }, [stopStream]);

  return { videoRef, phase, errMsg, devices, flip, retry };
}
